import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';

interface GroupMember {
  id: number;
  name: string;
  role: 'leader' | 'member' | 'analyst';
  performance: string;
  status: 'online' | 'offline';
}

interface TradingGroup {
  id: number;
  name: string;
  description: string;
  members: number;
  performance: string;
  risk: 'Low' | 'Medium' | 'High';
  category: string;
}

const MyGroup: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'joined' | 'discover'>('joined');

  const myGroups: TradingGroup[] = [
    {
      id: 1,
      name: "Crypto Scalpers Elite",
      description: "High-frequency trading focused on major cryptocurrencies",
      members: 127,
      performance: "+24.5%",
      risk: "High",
      category: "Scalping"
    },
    {
      id: 2,
      name: "DeFi Yield Hunters",
      description: "Long-term yield farming and liquidity provision strategies",
      members: 89,
      performance: "+18.2%",
      risk: "Medium",
      category: "DeFi"
    }
  ];

  const discoverGroups: TradingGroup[] = [
    {
      id: 3,
      name: "AI Quant Traders",
      description: "Algorithmic trading using machine learning models",
      members: 234,
      performance: "+31.7%",
      risk: "Medium",
      category: "Quantitative"
    },
    {
      id: 4,
      name: "Options Masters",
      description: "Advanced options trading strategies and hedging",
      members: 156,
      performance: "+19.8%",
      risk: "High",
      category: "Options"
    },
    {
      id: 5,
      name: "Swing Trade Pro",
      description: "Medium-term swing trading with technical analysis",
      members: 198,
      performance: "+15.4%",
      risk: "Low",
      category: "Swing Trading"
    }
  ];

  const groupMembers: GroupMember[] = [
    { id: 1, name: "AlphaTrader", role: "leader", performance: "+45.2%", status: "online" },
    { id: 2, name: "CryptoWolf", role: "analyst", performance: "+32.1%", status: "online" },
    { id: 3, name: "QuantMaster", role: "member", performance: "+28.7%", status: "offline" },
    { id: 4, name: "DeFiGuru", role: "member", performance: "+22.3%", status: "online" }
  ];

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'Low': return 'text-green-400';
      case 'Medium': return 'text-yellow-400';
      case 'High': return 'text-red-400';
      default: return 'text-green-400';
    }
  };

  return (
    <div className="h-full bg-[#0a0a1a] text-green-400 font-mono p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-green-400 mb-2">Trading Groups</h1>
        <div className="text-green-400/70 text-sm">
          Join groups, share strategies, and copy successful trades
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6">
        <Button
          onClick={() => setActiveTab('joined')}
          className={`px-4 py-2 text-sm rounded transition-colors ${
            activeTab === 'joined'
              ? 'bg-green-500/20 text-green-400 border border-green-500/30'
              : 'bg-transparent text-green-400/70 hover:bg-green-500/10 hover:text-green-400'
          }`}
        >
          My Groups ({myGroups.length})
        </Button>
        <Button
          onClick={() => setActiveTab('discover')}
          className={`px-4 py-2 text-sm rounded transition-colors ${
            activeTab === 'discover'
              ? 'bg-green-500/20 text-green-400 border border-green-500/30'
              : 'bg-transparent text-green-400/70 hover:bg-green-500/10 hover:text-green-400'
          }`}
        >
          Discover Groups
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Groups List */}
        <div className="lg:col-span-2">
          <div className="grid gap-4">
            {(activeTab === 'joined' ? myGroups : discoverGroups).map((group) => (
              <Card key={group.id} className="p-4 bg-[#0d0d2a] border-green-500/30 hover:border-green-400/50 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-green-300 font-semibold mb-1">{group.name}</h3>
                    <div className="text-xs text-green-500/70 mb-2">{group.category}</div>
                    <p className="text-green-400/70 text-sm">{group.description}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-green-300 font-bold">{group.performance}</div>
                    <div className="text-xs text-green-500/70">30d return</div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 text-xs text-green-400/70">
                    <span>👥 {group.members} members</span>
                    <span className={getRiskColor(group.risk)}>⚠️ {group.risk} Risk</span>
                  </div>
                  
                  <div className="flex space-x-2">
                    {activeTab === 'joined' ? (
                      <>
                        <Button size="sm" variant="outline" className="border-green-500/30 text-green-400/70 hover:bg-green-500/10 text-xs">
                          View
                        </Button>
                        <Button size="sm" className="bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30 text-xs">
                          Trade
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" className="bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30 text-xs">
                        Join Group
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Active Members (only show for joined groups) */}
          {activeTab === 'joined' && (
            <Card className="p-4 bg-[#0d0d2a] border-green-500/30">
              <h3 className="text-green-300 font-semibold mb-4">Active Members</h3>
              <div className="space-y-3">
                {groupMembers.map((member) => (
                  <div key={member.id} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${
                        member.status === 'online' ? 'bg-green-400' : 'bg-gray-500'
                      }`}></div>
                      <div>
                        <div className="text-green-400 text-sm">{member.name}</div>
                        <div className="text-green-500/50 text-xs capitalize">{member.role}</div>
                      </div>
                    </div>
                    <div className="text-green-300 text-xs">{member.performance}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Stats */}
          <Card className="p-4 bg-[#0d0d2a] border-green-500/30">
            <h3 className="text-green-300 font-semibold mb-4">Your Stats</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-green-400/70 text-sm">Groups Joined</span>
                <span className="text-green-300">{myGroups.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-400/70 text-sm">Avg Performance</span>
                <span className="text-green-300">+21.4%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-400/70 text-sm">Rank</span>
                <span className="text-green-300">#47</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MyGroup;
